import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@repletoryreach/sdk-auth';
import { Stack, Card, Title, Text, Button, Group } from '@repletoryreach/sdk-ui';
import { Alert, PageHeader } from '@repletoryreach/sdk-ui/data-display';
import { PasswordInput } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { TwoFactorPanel } from './components/TwoFactorPanel';
import { SessionsPanel } from './components/SessionsPanel';
import { SafeguardsPanel } from './components/SafeguardsPanel';
import { isPasswordValid, passwordRules, passwordRefusalMessage } from './password';
import { useT } from '../../../i18n/I18nProvider';
import './security.css';

export default function SettingsSecurityPage() {
  const t = useT();
  const { changePassword } = useAuth();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const rules = passwordRules(next);
  const valid = isPasswordValid(next);

  async function submit() {
    setError(null);
    if (!valid) { setError(t('settings.security.password.errors.weak')); return; }
    if (next !== confirm) { setError(t('settings.security.password.errors.mismatch')); return; }
    setBusy(true);
    try {
      await changePassword(current, next);
      setCurrent(''); setNext(''); setConfirm('');
      notifySuccess(t('settings.security.password.toast.changed'), t('settings.layout.nav.security'));
    } catch (e) {
      setError(passwordRefusalMessage(e, t) ?? (e instanceof Error ? e.message : t('settings.security.password.errors.failed')));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('common.settings')}
        title={t('settings.security.title')}
        description={t('settings.security.description')}
      />

      {/*
        Password and second factor are one decision made twice, so they sit
        side by side: an operator who has just rotated a password is the same
        operator who should notice that nothing else guards the account.
      */}
      <div className="rr-security-grid">
        <Card withBorder>
          <Stack gap="md">
            <Stack gap="xs">
              <Title order={5}>{t('settings.security.password.title')}</Title>
              <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('settings.security.password.subtitle')}</Text>
            </Stack>
            <PasswordInput
              label={t('settings.security.password.current')}
              value={current}
              onChange={(e) => setCurrent(e.currentTarget.value)}
              autoComplete="current-password"
            />
            <PasswordInput
              label={t('settings.security.password.new')}
              // The hint belongs under the control, and it states the rules the
              // identity service enforces rather than a prettier version of them.
              description={t('settings.security.password.hint')}
              value={next}
              onChange={(e) => setNext(e.currentTarget.value)}
              autoComplete="new-password"
            />
            {next.length > 0 && (
              <Stack gap="xs">
                {rules.map((rule) => (
                  <Text
                    key={rule.key}
                    role="caption"
                    c={rule.met ? 'var(--rr-color-success)' : 'var(--rr-color-text-muted)'}
                  >
                    {rule.met ? '✓' : '○'} {t(rule.key)}
                  </Text>
                ))}
              </Stack>
            )}
            <PasswordInput
              label={t('settings.security.password.confirm')}
              value={confirm}
              onChange={(e) => setConfirm(e.currentTarget.value)}
              autoComplete="new-password"
            />
            {error && <Alert color="error">{error}</Alert>}
            <Group gap="sm" justify="flex-end">
              <Button onClick={submit} loading={busy} disabled={!current || !valid || !confirm}>
                {t('settings.security.password.submit')}
              </Button>
            </Group>
          </Stack>
        </Card>

        <TwoFactorPanel />
      </div>

      <div className="rr-security-grid">
        <SafeguardsPanel />
        <SessionsPanel />
      </div>

      {/*
        API keys are the platform's other credential surface. The link used to
        be a card of its own, which gave a signpost the same weight as the
        panels that actually do something.
      */}
      <div className="rr-security-crosslink">
        <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('settings.security.credentials.question')}</Text>
        <Link to="/settings/api-keys" className="rr-security-crosslink__link">
          {t('settings.security.credentials.link')}
        </Link>
      </div>
    </Stack>
  );
}
